import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Breadcrumbs, Button, Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { darken, styled, Theme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Icontroller, IlocationInfo, IroomTypes, IroomsList, MqttStatus } from '../../API/types'
import { DataGrid, GridColDef, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid'
import { DoorSliding, Home, Settings, MobileScreenShare } from '@mui/icons-material'
import RoomControllerStatus from '../Controllers/roomControllerStatus'
//import { appUrl } from '../../API/utils'
import mqttService from '../../API/mqttService '
import DoorAccess from './DoorAccess'

const useStyles = makeStyles()(({ spacing }) => ({
  box: {
    margin: spacing(0.5),
  },
}))

const getBackgroundColor = (color: string, theme: Theme, coefficient: number) => ({
  backgroundColor: darken(color, coefficient),
})

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .status-theme-online': {
    ...getBackgroundColor(theme.palette.success.main, theme, 0.7),
    '&:hover': {
      ...getBackgroundColor(theme.palette.success.main, theme, 0.6),
    },
    '&.Mui-selected': {
      ...getBackgroundColor(theme.palette.success.main, theme, 0.5),
      '&:hover': {
        ...getBackgroundColor(theme.palette.success.main, theme, 0.4),
      },
    },
  },
  '& .status-theme-offline': {
    ...getBackgroundColor(theme.palette.error.main, theme, 0.7),
    '&:hover': {
      ...getBackgroundColor(theme.palette.error.main, theme, 0.6),
    },
    '&.Mui-selected': {
      ...getBackgroundColor(theme.palette.error.main, theme, 0.5),
      '&:hover': {
        ...getBackgroundColor(theme.palette.error.main, theme, 0.4),
      },
    },
  },
}))

interface RoomsListTableProps {
  locationData: IlocationInfo
  controllers: Icontroller[]
  roomTypesData: IroomTypes[]
}

const RoomsListTable: FC<RoomsListTableProps> = ({ locationData, controllers, roomTypesData }) => {
  const { t } = useTranslation('')
  const { classes } = useStyles()

  const dataGridRef = useGridApiRef()

  const [openDoorAccess, setOpenDoorAccess] = useState<number | null>(null)
  const [mqttStatus, setMqttStatus] = useState<MqttStatus[]>([])

  const handleMessage = useCallback((topic: string, message: string) => {
    const dateNow = new Date().toLocaleString('en-GB')
    try {
      const msgJson: MqttStatus = JSON.parse(message)
      msgJson.deviceId = msgJson.deviceId.toString()

      console.log(topic, message)

      setMqttStatus((previous) => {
        const newMqttStatus = [...previous]

        const mqttTopic = topic.split('/')
        const device = newMqttStatus.find((device) => device.deviceId === mqttTopic[1])

        if (device) {
          if (mqttTopic[2] === 'doorLock') {
            device.lockDuration = msgJson.lockDuration
            device.lockStatus = msgJson.lockStatus
          } else if (mqttTopic[2] === 'doorStatus') {
            device.doorStatus = msgJson.doorStatus
          } else if (mqttTopic[2] === 'status') {
            device.humidity = msgJson.humidity
            device.tempC = msgJson.tempC
          } else if (mqttTopic[2] === 'online') {
            device.online = msgJson.online
          }

          //console.log('update')
        } else {
          //console.log('add new')
          newMqttStatus.push(msgJson)
        }

        return newMqttStatus
      })
    } catch (e) {
      console.log(dateNow, topic, message)
    }
  }, [])

  //console.log(mqttStatus);

  const handleClickOpenDoorAccess = useCallback((roomId: number) => {
    setOpenDoorAccess(roomId)
  }, [])

  const handleClickCloseDoorAccess = useCallback(() => {
    setOpenDoorAccess(null)
  }, [])

  useEffect(() => {
    const client = mqttService.getClient()
    mqttService.onMessage(client, handleMessage)
    mqttService.subscribe(client, 'device/#')
  }, [handleMessage])

  const roomsList = useMemo(() => {
    const length = locationData?.collection?.length || 0
    let roomsList: IroomsList[] = []
    for (let i = 0; i < length; i++) {
      if (locationData?.collection[i] && locationData?.collection[i]?.roomsList) {
        roomsList = roomsList.concat(locationData?.collection[i]?.roomsList || [])
      }
    }

    return roomsList.filter((row) => row.roomTypeId !== 0)
  }, [locationData?.collection])

  //console.log(roomsList);

  const columns: GridColDef[] = [
    /*
    { field: 'online', headerName: 'online', width: 150, 
      renderCell: (params: GridRenderCellParams) => {

        const rowlocation = locationData.collection.find((location) => location.id === params.row?.locationId)
        const mqttDeviceStatus = mqttStatus.find(
          (device) => device.deviceId === rowlocation?.controllerLogin?.toString(),
        )

        return (
          mqttDeviceStatus?.online ? 'on' : 'off'
        )
      },
     },
     */
    { field: 'roomName', headerName: 'Telpas nosaukums', width: 150 },
    {
      field: 'roomTypeId',
      headerName: 'Telpas tips',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const currentroomType = roomTypesData.find((roomType) => roomType.id === params.row?.roomTypeId)
        return (
          <>
            {currentroomType?.technicalRoom && <Settings />}
            {currentroomType?.publicDoor && <DoorSliding />}
            {!currentroomType?.technicalRoom && !currentroomType?.publicDoor ? <Home /> : null}
            &nbsp; -&nbsp; {currentroomType?.roomTypeName}
          </>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const rowlocation = locationData.collection.find((location) => location.id === params.row?.locationId)
        const mqttDeviceStatus = mqttStatus.find(
          (device) => device.deviceId === rowlocation?.controllerLogin?.toString(),
        )

        return (
          rowlocation?.controllerId && (
            <RoomControllerStatus controllerId={rowlocation?.controllerId} mqttDeviceStatus={mqttDeviceStatus} />
          )
        )
      },
    },
    {
      field: 'access',
      headerName: 'Access',
      width: 220,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Button
            variant='outlined'
            startIcon={<MobileScreenShare />}
            onClick={() => handleClickOpenDoorAccess(params.row?.id)}
          >
            {t('Door access')}
          </Button>
        )
      },
    },
  ]

  return (
    <>
      {openDoorAccess !== null && (
        <DoorAccess roomsList={roomsList} roomId={openDoorAccess} handleClickClose={handleClickCloseDoorAccess} />
      )}
      <Box
        sx={
          {
            //display: 'flex',
            //flexWrap: 'wrap',
            //maxWidth: 300,
          }
        }
      >
        <Box className={classes.box}>
          <Breadcrumbs aria-label='breadcrumb'>
            <Link underline='hover' color='inherit'>
              {t('Telpas lokācija - ')}
              {locationData?.currentLocation[locationData?.currentLocation.length - 1]?.locationName || ''}
            </Link>
          </Breadcrumbs>
        </Box>
        <Box className={classes.box}>
          <StyledDataGrid
            apiRef={dataGridRef}
            rows={roomsList}
            columns={columns}
            rowSelection={false}
            disableColumnMenu={true}
            hideFooter={true}
            autoHeight={true}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 100 },
              },
            }}
            getRowClassName={(params) => {
              const rowlocation = locationData.collection.find((location) => location.id === params.row?.locationId)
              const mqttDeviceStatus = mqttStatus.find(
                (device) => device.deviceId === rowlocation?.controllerLogin?.toString(),
              )

              return mqttDeviceStatus?.online ? '' : 'status-theme-offline'
            }}
            pageSizeOptions={[50, 10]}
          />
        </Box>
      </Box>
    </>
  )
}

export default RoomsListTable
